import { addNewUserValidations } from './validations';
import Dealership from './Dealership/Dealership';
import Input from 'src/components/forms/input';
import { IUser } from '../../../../../../utils/interfaces/user';
import Spinner from 'src/components/ui/spinner/index';
import { useFormik } from 'formik';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    DealerAssociation,
    DealerPermission,
} from 'src/services/models/dealer-management.service';
import {
    DealerManagementService,
    IAddDealerUserPayload,
} from 'src/services/dealer-management.service';
import React, { Dispatch, SetStateAction, useState } from 'react';

import './styles.scss';

const EMPTY_ARRAY_SIZE = 0;
interface IFormProps {
    backClickHandler: Function;
    setSuccessNotification: Dispatch<SetStateAction<boolean>>;
    setErrorNotification: Dispatch<SetStateAction<boolean>>;
    dealerPermissions: Array<DealerPermission>;
    dealerAssociations: Array<DealerAssociation>;
    isDSMSearch?: boolean;
}

const Form: React.FC<IFormProps> = ({
    backClickHandler,
    setSuccessNotification,
    setErrorNotification,
    dealerPermissions,
    dealerAssociations,
    isDSMSearch = false,
}) => {
    const { currentDealers, dealerDataInfo, dealerGroupState } = useSelector(
        (state: IUser) => {
            return state;
        },
    );
    const brand: string = dealerDataInfo?.dealer.brand || 'vw';

    const [submitErrors, setSubmitErrors] = useState({
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
    });
    const [selectedDealerships, setSelectedDealerships] = useState<
        Array<string>
    >([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [permissionsMap, setPermissionsMap] = useState<
        Map<string, Array<string>>
    >(new Map());
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
    } = useFormik({
        initialValues: {
            email: '',
            phone: '',
            firstName: '',
            lastName: '',
        },
        validationSchema: addNewUserValidations,
        onSubmit: handlerSubmit,
    });
    const { addNewUser } = useLocalization();

    async function handlerSubmit(): Promise<void> {
        const permissionsObj = getPermissionsObj(
            permissionsMap,
            currentDealers?.default,
        );
        setLoading(true);
        setErrorNotification(false);
        setSubmitErrors({
            email: '',
            phone: '',
            firstName: '',
            lastName: '',
        });

        if (
            permissionsObj.permissions.length > EMPTY_ARRAY_SIZE &&
            permissionsObj.associations.length > EMPTY_ARRAY_SIZE
        ) {
            const service = new DealerManagementService();
            const country =
                dealerGroupState &&
                dealerGroupState[0]?.dealers[0]?.country === 'US'
                    ? 'USA'
                    : 'Canada';
            const request = {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                emailAddress: values.email.toLowerCase(),
                phoneNumber: values.phone.replace(/\D/g, ''),
                isFieldAccessUser: false,
                country: country || '',
                dealerId: permissionsObj.dealerId,
                permissions: permissionsObj.permissions,
                associations: permissionsObj.associations,
            };
            try {
                await service.addNewUser(request as IAddDealerUserPayload);
                setLoading(false);
                backClickHandler();
                setSuccessNotification(true);
            } catch (error) {
                setLoading(false);
                setErrorNotification(true);
            }
        } else {
            setLoading(false);
            setErrorNotification(true);
        }
    }

    const onChangePhoneHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const formattedPhone = addHyphens(event.target.value);
        setFieldValue(event.target.name, formattedPhone);
    };

    return (
        <form
            className={'new-user-container__body--form'}
            onSubmit={handleSubmit}
        >
            <Input
                labelName={addNewUser.FORM_LABEL_FIRSTNAME}
                type={'text'}
                name={'firstName'}
                maxlength={30}
                handlerChange={handleChange}
                handlerBlur={handleBlur}
                value={values.firstName}
                asErrors={errors.firstName}
                asTouched={touched.firstName}
                asSubmitError={submitErrors.firstName}
            />
            <Input
                labelName={addNewUser.FORM_LABEL_LASTNAME}
                type={'text'}
                name={'lastName'}
                maxlength={30}
                handlerChange={handleChange}
                handlerBlur={handleBlur}
                value={values.lastName}
                asErrors={errors.lastName}
                asTouched={touched.lastName}
                asSubmitError={submitErrors.lastName}
            />
            <Input
                labelName={addNewUser.FORM_LABEL_PHONE}
                type={'text'}
                name={'phone'}
                maxlength={12}
                handlerChange={onChangePhoneHandler}
                handlerBlur={handleBlur}
                value={values.phone}
                asErrors={errors.phone}
                asTouched={touched.phone}
                asSubmitError={submitErrors.phone}
                handleKeyPress={onKeyPressPhoneHandler}
            />
            <Input
                labelName={addNewUser.FORM_LABEL_EMAIL}
                type={'text'}
                name={'email'}
                maxlength={50}
                handlerChange={handleChange}
                handlerBlur={handleBlur}
                value={values.email}
                asErrors={errors.email}
                asTouched={touched.email}
                asSubmitError={submitErrors.email}
            />
            <Dealership
                selectedDealerships={selectedDealerships}
                onSelectDealerships={setSelectedDealerships}
                permissionsMap={permissionsMap}
                setPermissionsMap={setPermissionsMap}
                dealerPermissionsFromToken={dealerPermissions}
                dealerAssociationsFromToken={dealerAssociations}
                isDSMSearch={isDSMSearch}
            />
            <div className='new-user--form_button-ct'>
                <button
                    className={`new-user--form_button c-btn ${brand.toLowerCase()}`}
                    disabled={
                        values.email === '' ||
                        values.firstName === '' ||
                        values.lastName === '' ||
                        values.phone === '' ||
                        !!Object.keys(errors).length ||
                        !Object.keys(selectedDealerships).length ||
                        !Array.from(permissionsMap.values()).some(
                            (perms) => perms.length > EMPTY_ARRAY_SIZE,
                        ) ||
                        loading
                    }
                    type={'submit'}
                >
                    <span className='c-btn__text'>
                        {loading ? <Spinner /> : addNewUser.BUTTON_ADD_LABEL}
                    </span>
                </button>
            </div>
        </form>
    );
};

export function addHyphens(phone: string) {
    phone = phone.replace(/\D/g, '');

    phone = phone.replace(
        /^(\d{3})(\d{1,3})?(\d{1,4})?/,
        (match, p1, p2, p3) => {
            let result = p1;
            if (p2) {
                result += '-' + p2;
            }
            if (p3) {
                result += '-' + p3;
            }

            return result;
        },
    );

    return phone;
}

export function getPermissionsObj(
    permissionsMap: Map<string, Array<string>>,
    defaultDealer: string | undefined,
): {
    permissions: Array<{ dealerId: string; permission: string }>;
    associations: Array<{ dealerId: string }>;
    dealerId: string;
    arSelected: boolean;
} {
    const permissions: Array<{ dealerId: string; permission: string }> = [];
    const associations: Array<{ dealerId: string }> = [];
    let dealerId = '';
    let arSelected = permissionsMap.size > EMPTY_ARRAY_SIZE ? true : false;

    permissionsMap.forEach((values, key) => {
        if (values.length > EMPTY_ARRAY_SIZE) {
            associations.push({ dealerId: key });
            if (key == defaultDealer) {
                dealerId = key;
            }
            values.forEach((p) => {
                permissions.push({ dealerId: key, permission: p });
            });
        } else {
            arSelected = false;
        }
    });

    if (!dealerId && associations.length > EMPTY_ARRAY_SIZE) {
        dealerId = associations[0].dealerId;
    }
    return { permissions, associations, dealerId, arSelected };
}

export const onKeyPressPhoneHandler = (event: any) => {
    const charCode = event.which ? event.which : event.keyCode;
    const backspaceKeyCode = 8;
    const tabKeyCode = 9;
    const deleteKeyCode = 46;
    const firstNumberKeyCode = 48;
    const lastNumberKeyCode = 57;
    const firstNumPadKeyCode = 96;
    const lastNumPadKeyCode = 105;

    if (
        charCode == backspaceKeyCode ||
        charCode == deleteKeyCode ||
        charCode == tabKeyCode
    ) {
        return true;
    }

    if (
        charCode < firstNumberKeyCode ||
        (charCode > lastNumberKeyCode && charCode < firstNumPadKeyCode) ||
        charCode > lastNumPadKeyCode
    )
        event.preventDefault();
};

export default Form;
